import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { money } from "../../lib/api";

const TOOLTIP = { background: "#0b0f19", border: "1px solid rgba(212,175,55,.3)" };
const TYPE_FILL = { STAR: "#d4af37", MOON: "#4f46e5" };

const Kpi = ({ label, value, testid }) => (
  <div className="hy-glass p-6" data-testid={testid}>
    <div className="hy-label">{label}</div>
    <div className="font-display text-2xl text-amber-200 mt-3">{value}</div>
  </div>
);

const Panel = ({ title, children, className = "", testid }) => (
  <div className={`hy-card p-7 ${className}`} data-testid={testid}>
    <div className="hy-label mb-6">{title}</div>
    {children}
  </div>
);

export const AnalyticsTab = ({ stats, insights }) => (
  <div data-testid="admin-analytics">
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
      <Kpi label="Total Revenue" value={money(stats.total_revenue)} testid="kpi-revenue" />
      <Kpi label="Confirmed Records" value={stats.total_registries} testid="kpi-records" />
      <Kpi label="Avg Order Value" value={money(stats.avg_order_value)} testid="kpi-aov" />
      <Kpi label="Customers" value={stats.total_users} testid="kpi-users" />
      <Kpi label="Pending Payment" value={stats.pending_registries} testid="kpi-pending" />
      <Kpi label="Discounts Given" value={money(stats.discount_given)} testid="kpi-discount" />
      <Kpi label="Next Registry #" value={`#${String(stats.next_registry_number).padStart(3, "0")}`} testid="kpi-next" />
    </div>

    <div className="grid lg:grid-cols-3 gap-6 mt-10">
      <Panel title="Revenue by Edition" className="lg:col-span-2">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={stats.revenue_by_edition}>
            <XAxis dataKey="edition" stroke="#94a3b8" fontSize={11} />
            <YAxis stroke="#94a3b8" fontSize={11} />
            <Tooltip contentStyle={TOOLTIP} />
            <Bar dataKey="revenue" fill="#d4af37" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="Records by Type">
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={stats.registries_by_type} dataKey="count" nameKey="type" innerRadius={55} outerRadius={90}>
              {stats.registries_by_type.map((entry) => (
                <Cell key={entry.type} fill={TYPE_FILL[entry.type] || "#94a3b8"} />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP} />
          </PieChart>
        </ResponsiveContainer>
      </Panel>
    </div>

    <Panel title="Revenue Trend" className="mt-6">
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={stats.revenue_by_month}>
          <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
          <YAxis stroke="#94a3b8" fontSize={11} />
          <Tooltip contentStyle={TOOLTIP} />
          <Line type="monotone" dataKey="revenue" stroke="#f5d061" strokeWidth={2} dot={{ fill: "#d4af37" }} />
        </LineChart>
      </ResponsiveContainer>
    </Panel>

    <PromoLeaderboard insights={insights} />
  </div>
);

const MEDAL = ["text-amber-200", "text-slate-300", "text-amber-700/90"];

const PromoLeaderboard = ({ insights }) => {
  if (!insights)
    return (
      <Panel title="Promo Leaderboard" className="mt-6" testid="promo-leaderboard">
        <p className="text-sm text-slate-500" data-testid="promo-leaderboard-unavailable">
          Promo performance could not be loaded. Reload the page to try again.
        </p>
      </Panel>
    );
  const top = [...insights.codes]
    .filter((c) => c.registrations > 0)
    .sort((a, b) => b.registrations - a.registrations || b.net_revenue - a.net_revenue)
    .slice(0, 5);

  return (
    <Panel title="Promo Leaderboard" className="mt-6" testid="promo-leaderboard">
      <p className="text-xs text-slate-500 -mt-3 mb-6">
        Best-performing codes by registrations. {insights.totals.promo_share}% of all confirmed
        registrations used a code, costing {money(insights.totals.discount_given)} in discounts.
      </p>
      {top.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="promo-leaderboard-empty">
          No promotional code has been redeemed yet.
        </p>
      ) : (
        <div className="space-y-3">
          {top.map((c, i) => (
            <div key={c.code} className="p-4 border border-amber-500/15 flex items-start gap-4"
              data-testid={`leaderboard-row-${c.code}`}>
              <span className={`font-display text-xl w-7 shrink-0 ${MEDAL[i] || "text-slate-500"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="min-w-0 sm:flex-1">
                  <div className="font-mono-hy text-amber-200 text-sm break-all">{c.code}</div>
                  <div className="text-[0.65rem] text-slate-500 mt-1 leading-relaxed">
                    {c.registrations} registration{c.registrations > 1 ? "s" : ""}
                    {" · "}{c.by_edition.map((e) => `${e.edition} ${e.count}`).join(" · ")}
                    {c.active ? "" : " · inactive"}
                  </div>
                </div>
                <div className="flex gap-8 sm:gap-6 shrink-0">
                  <div className="sm:text-right sm:w-32">
                    <div className="text-emerald-300 text-sm">{money(c.net_revenue)}</div>
                    <div className="text-[0.65rem] text-slate-500 mt-1">net revenue</div>
                  </div>
                  <div className="sm:text-right sm:w-28">
                    <div className="text-violet-300 text-sm">−{money(c.discount)}</div>
                    <div className="text-[0.65rem] text-slate-500 mt-1">{c.discount_rate}% discount</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
